import React, { useState, useEffect } from 'react';
import { 
  AppBar, Toolbar, IconButton, Typography, Box, 
  Drawer, List, ListItem, ListItemIcon, ListItemText, 
  Divider, Avatar, Menu, MenuItem, Button, Tooltip,
  useTheme, useMediaQuery, Fade, Zoom
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';

const Navigation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useSupabaseAuth();
  const [scrolled, setScrolled] = useState(false);
  
  const menuOpen = Boolean(anchorEl);
  
  // Detect scroll position for elevation effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navItems = [
    { name: 'Home', path: '/', icon: <HomeIcon /> },
    { name: 'Recipe Generator', path: '/recipe-generator', icon: <RestaurantIcon /> },
    { name: 'Meal Planner', path: '/meal-planner', icon: <EventNoteIcon /> },
    { name: 'Meal Calendar', path: '/meal-calendar', icon: <CalendarMonthIcon /> },
  ];
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      handleCloseUserMenu();
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const isActive = (path) => {
    // Handle exact match for home
    if (path === '/' && location.pathname === '/') return true;
    // Handle other paths (including nested paths)
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Enhanced drawer with animations and better spacing
  const drawer = (
    <Box sx={{ 
      textAlign: 'center', 
      width: 270,
      height: '100%',
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        py: 2.5, 
        px: 2, 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Meal Maestro
        </Typography>
      </Box>
      
      <List sx={{ flexGrow: 1, py: 1 }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem 
              key={item.name} 
              component={RouterLink} 
              to={item.path} 
              button
              onClick={handleDrawerToggle}
              selected={active}
              sx={{ 
                my: 0.5,
                mx: 1,
                borderRadius: 1.5,
                color: active ? 'primary.main' : 'text.primary',
                bgcolor: active ? 'action.selected' : 'transparent',
                '&:hover': {
                  bgcolor: active ? 'action.selected' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ color: active ? 'primary.main' : 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItem>
          );
        })}
      </List>
      
      {/* User section in drawer for mobile */}
      <Box sx={{ p: 2, mt: 'auto', borderTop: `1px solid ${theme.palette.divider}` }}>
        {user ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', mr: 1.5 }}
                src={user.user_metadata?.avatar_url}
                alt={user.user_metadata?.name || user.email}
              >
                {(user.user_metadata?.name || user.email || '?')[0].toUpperCase()}
              </Avatar>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {user.user_metadata?.name || user.email}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                fullWidth 
                startIcon={<AccountCircleIcon />}
                size="small"
                onClick={() => {
                  navigate('/profile');
                  handleDrawerToggle();
                }}
              >
                Profile
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                color="error" 
                startIcon={<LogoutIcon />}
                size="small"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              fullWidth
              startIcon={<LoginIcon />}
              onClick={() => {
                navigate('/login');
                handleDrawerToggle();
              }}
            >
              Login
            </Button>
            <Button 
              variant="contained" 
              fullWidth
              startIcon={<PersonAddIcon />}
              onClick={() => {
                navigate('/signup');
                handleDrawerToggle();
              }}
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar 
        position="sticky" 
        color="default" 
        elevation={scrolled ? 2 : 0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          transition: 'all 0.3s ease',
          borderBottom: scrolled ? 'none' : `1px solid ${theme.palette.divider}`
        }}
      >
        <Toolbar sx={{ height: 64 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1, 
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 700,
              letterSpacing: 0.2
            }}
          >
            <RestaurantIcon sx={{ mr: 1, color: 'primary.main' }} />
            Meal Maestro
          </Typography>
          
          <Box 
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5
            }}
          >
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Button
                  key={item.name}
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{ 
                    mx: 0.5, 
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    color: active ? 'primary.main' : 'text.primary',
                    fontWeight: active ? 600 : 400,
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      bgcolor: active ? 'primary.main' : 'action.hover',
                      color: active ? 'white' : 'text.primary',
                    },
                    transition: 'color 0.3s ease, background-color 0.3s ease',
                    '&::after': active ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '15%',
                      width: '70%',
                      height: 3,
                      bgcolor: 'primary.main',
                      borderRadius: 4,
                    } : {}
                  }}
                >
                  {item.name}
                </Button>
              );
            })}
          </Box>
          
          <Box sx={{ ml: 2 }}>
            {user ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Account settings">
                  <IconButton 
                    onClick={handleMenuOpen} 
                    size="small" 
                    sx={{ 
                      ml: 2,
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)'
                      }
                    }}
                    aria-controls={menuOpen ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={menuOpen ? 'true' : undefined}
                  >
                    <Avatar 
                      sx={{ 
                        width: 36, 
                        height: 36, 
                        bgcolor: 'primary.main',
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}
                      src={user.user_metadata?.avatar_url}
                      alt={user.user_metadata?.name || user.email}
                    >
                      {(user.user_metadata?.name || user.email || '?')[0].toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={menuOpen}
                  onClose={handleCloseUserMenu}
                  onClick={handleCloseUserMenu}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 2,
                    sx: {
                      overflow: 'visible',
                      mt: 1.5,
                      width: 200,
                      borderRadius: 2,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  TransitionComponent={Fade}
                  transitionDuration={250}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Signed in as
                    </Typography>
                    <Typography variant="subtitle2" noWrap fontWeight={600}>
                      {user.email}
                    </Typography>
                  </Box>
                  
                  <Divider />
                  
                  <MenuItem 
                    onClick={() => navigate('/profile')}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Profile" />
                  </MenuItem>
                  
                  <Divider />
                  
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{ py: 1.5, color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                  <Button 
                    color="inherit" 
                    component={RouterLink} 
                    to="/login"
                    startIcon={<LoginIcon />}
                    sx={{ 
                      borderRadius: 1.5,
                      py: 0.8
                    }}
                  >
                    Login
                  </Button>
                </Zoom>
                
                <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    component={RouterLink}
                    to="/signup"
                    startIcon={<PersonAddIcon />}
                    sx={{ 
                      borderRadius: 1.5,
                      py: 0.8,
                      boxShadow: 2
                    }}
                  >
                    Sign Up
                  </Button>
                </Zoom>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 270,
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Navigation;
